import {
  calculateRlAggregate,
  formatContactDisplayName,
  getInitials,
  isAccountTeamVendorLabel,
  meddpiccCanonicalToMatrixKey,
  meddpiccRoleToMatrixColumn,
  MEDD_MATRIX_ROLE_DEFS,
  mergeAccountRelationshipBundle,
  normalizePairVendorFields,
  parseAlignmentLevel,
} from "./relationshipUtils";
import { normalizeUnknownMeddWeight } from "../../../Utils/Helper/rlFanCaps";

const prefer = (...values) =>
  values.find(value => value !== undefined && value !== null && value !== "");

export const buildDefaultVendorContext = () => ({
  teamMembers: [],
  executivesById: new Map(),
  assignmentByAccountId: new Map(),
  sponsorsByAccountId: new Map(),
  personalPairAccountIds: new Set(),
});

const normalizeAccount = row => ({
  id: String(row.id || row.customer_id || row.account_id || row.accountId),
  name: prefer(
    row.name,
    row.account_name,
    row.accountName,
    "Unnamed Account"
  ).trim(),
  initials: getInitials(
    prefer(row.name, row.account_name, row.accountName, "NA")
  ),
  industry: prefer(row.industry, "Unknown"),
  tier: prefer(row.tier, row.tiers, "Portfolio"),
  region: prefer(row.region, row.country, "Unknown"),
  alLevel: (() => {
    const alRaw = prefer(row.latestLevel, row.level, row.current_level);
    if (alRaw === undefined || alRaw === null || alRaw === "") return null;
    return parseAlignmentLevel(alRaw);
  })(),
  rlAggregate: (() => {
    const raw = prefer(row.rlAggregate, row.aggregateRl, row.aggregate_rl);
    if (raw === undefined || raw === null || raw === "") return null;
    const numeric = Number(raw);
    return Number.isFinite(numeric) ? numeric : null;
  })(),
  mappedPairs: Number(row.mappedPairs) || 0,
  assessedPairs: Number(row.assessedPairs) || 0,
  pendingAssessments: Number(row.pendingAssessments) || 0,
  coveragePercent: Number(row.coveragePercent) || 0,
  mappingPossiblePairs: Number(row.mappingPossiblePairs) || 0,
  customerTeamMemberCount: Number(row.customerTeamMemberCount) || 0,
  raw: row,
});

export const mapRelationshipAssessmentAccount = row =>
  normalizeAccount({
    id: row.accountId,
    account_id: row.accountId,
    name: row.accountName,
    industry: row.industryVertical,
    tier: row.tier,
    region: row.region,
    level: row.latestLevel,
    rlAggregate: prefer(row.aggregateRl, row.aggregateRlMedian),
    mappedPairs: row.mappingCount ?? 0,
    assessedPairs: row.rlAssessmentCount ?? 0,
    pendingAssessments: row.pendingRl ?? 0,
    mappingPossiblePairs: row.totalPossibleMapping ?? 0,
    customerTeamMemberCount: row.customerTeamMemberCount ?? 0,
    coveragePercent: (() => {
      if (row.engagementScore != null && row.engagementScore !== "") {
        return Number(row.engagementScore) || 0;
      }
      if (row.relationshipStrength != null && row.relationshipStrength !== "") {
        return Number(row.relationshipStrength) || 0;
      }
      const mapped = Number(row.mappingCount) || 0;
      const assessed = Number(row.rlAssessmentCount) || 0;
      if (mapped > 0) return Math.round((assessed / mapped) * 100);
      const total = Number(row.totalPossibleMapping) || 0;
      if (total > 0) return Math.round((mapped / total) * 100);
      return 0;
    })(),
    raw: row,
  });

const mapViewContact = c => {
  const meddpiccRoleCanonical = prefer(
    c.meddpiccRoleCanonical,
    c.meddpiccRole,
    c.meddpicc_role,
    c.role,
    "unknown"
  );
  const meddKey = meddpiccCanonicalToMatrixKey(meddpiccRoleCanonical);
  const matrixRoleLabel = MEDD_MATRIX_ROLE_DEFS[meddKey]?.label;
  return {
    id: String(c.id),
    accountId: "",
    name: formatContactDisplayName(c),
    title: prefer(c.title, c.position, ""),
    meddpiccRoleCanonical,
    meddpiccRole:
      meddKey !== "UN" && matrixRoleLabel
        ? matrixRoleLabel
        : meddpiccRoleToMatrixColumn(meddpiccRoleCanonical),
    posture: prefer(c.posture, "Unknown"),
    status: "active",
    powerLevel: c.powerLevel,
    raw: {
      ...c,
      legacy_customer_team_member_id: prefer(
        c.legacyCustomerTeamMemberId,
        c.legacy_customer_team_member_id,
        c.id
      ),
    },
  };
};

const mapViewVendor = v => {
  const isTeam =
    v.vendorType === "account_team_member" ||
    v.vendorType === "team_member" ||
    isAccountTeamVendorLabel(v.label);
  const isSponsor = v.vendorType === "sponsor";
  const roleCode = isTeam
    ? prefer(v.role, v.functional_role, "")
    : prefer(v.role, "");
  const memberTitle = isTeam
    ? prefer(v.member_title, v.memberTitle, v.title, "")
    : "";
  const execType = String(
    prefer(v.type, v.sponsorRole, v.executive_type, v.raw?.executive_type, "")
  ).toLowerCase();
  const sponsorType =
    execType === "primary" || execType === "secondary" ? execType : undefined;
  return {
    id: isTeam ? `team-${v.id}` : `exec-${v.id}`,
    sourceId: String(v.id),
    accountId: "",
    name: prefer(v.name, "Vendor"),
    member_title: memberTitle,
    title: isTeam ? roleCode || memberTitle : prefer(v.title, v.role, "Vendor"),
    ladderLevel: Number(prefer(v.ladderLevel, v.ladder_level, 2)) || 2,
    role: isTeam ? roleCode : prefer(v.role, "Vendor"),
    vendorType: isTeam ? "team_member" : isSponsor ? "sponsor" : "executive",
    ...(sponsorType ? { type: sponsorType, sponsorRole: sponsorType } : {}),
    raw: v,
  };
};

const mapViewPair = (p, accountId) =>
  normalizePairVendorFields({
    id: String(p.id),
    accountId: String(accountId),
    vendorType:
      p.vendorType === "team_member"
        ? "team_member"
        : p.vendorType === "sponsor"
          ? "sponsor"
          : "executive",
    vendorSourceId: p.vendorId ? String(p.vendorId) : null,
    vendorStakeholderId: null,
    contactId: String(
      prefer(p.contactId, p.account_contact_id, p.accountContactId, "")
    ),
    ladderLevel: Number(prefer(p.ladderLevel, 2)) || 2,
    rlLevel: p.rlScore ?? p.rlLevel ?? null,
    assessedPosture: p.assessedPosture ?? null,
    rationale: p.rationale ?? null,
    assessmentAssignmentId: p.assignmentId ?? null,
    assessedAt: p.assessedAt ?? null,
    goalTargetRl: p.targetRlScore ?? p.goalTargetRl ?? null,
    goalTargetHorizon: p.goalTargetHorizon ?? null,
    reassessBy: p.reassessBy ?? null,
    isPrimaryOwner: p.isPrimaryOwner !== false,
    pendingConfirm: Boolean(p.pending_confirm ?? p.pendingConfirm),
    precededByUserId: prefer(p.precededByUserId, p.preceded_by_user_id, null)
      ? String(prefer(p.precededByUserId, p.preceded_by_user_id))
      : null,
    precededByUserName: prefer(
      p.precededByUserName,
      p.preceded_by_user_name,
      null
    ),
    precededByRelationshipId: prefer(
      p.precededByRelationshipId,
      p.preceded_by_relationship_id,
      null
    )
      ? String(
          prefer(p.precededByRelationshipId, p.preceded_by_relationship_id)
        )
      : null,
    predecessorRl: prefer(p.predecessorRl, p.predecessor_rl, null),
    predecessorGoalTargetRl: prefer(
      p.predecessorGoalTargetRl,
      p.predecessor_goal_target_rl,
      null
    ),
    predecessorGoalHorizonMonths: prefer(
      p.predecessorGoalHorizonMonths,
      p.predecessor_goal_horizon_months,
      null
    ),
    predecessorHistoryCount: Number(
      prefer(p.predecessorHistoryCount, p.predecessor_history_count, 0) || 0
    ),
    relationshipGoal: null,
    isOrphaned: false,
    lastTouchDays: 0,
    signalStrength: 0,
    raw: {
      ...p,
      account_contact_id: prefer(
        p.contactId,
        p.account_contact_id,
        p.accountContactId
      ),
      account_team_member_id: prefer(
        p.accountTeamMemberId,
        p.account_team_member_id,
        p.vendorId
      ),
      executive_id: prefer(p.executiveId, p.executive_id),
    },
  });

const mergePairFromRlSummary = (pair, summary) => {
  if (!summary) return normalizePairVendorFields(pair);
  return normalizePairVendorFields({
    ...pair,
    rlLevel: summary.rlScore ?? pair.rlLevel,
    assessedPosture: summary.assessedPosture ?? pair.assessedPosture,
    rationale: summary.rationale ?? pair.rationale,
    assessmentAssignmentId: summary.assignmentId ?? pair.assessmentAssignmentId,
    assessedAt: summary.assessedAt ?? pair.assessedAt,
    goalTargetRl: summary.targetRlScore ?? pair.goalTargetRl,
    goalTargetHorizon: summary.goalTargetHorizon ?? pair.goalTargetHorizon,
    reassessBy: summary.reassessBy ?? pair.reassessBy,
  });
};

const normalizeAggregateSettings = settings => ({
  consider_meddpicc_role: settings?.consider_meddpicc_role !== false,
  unknown_meddpicc_default_weight: normalizeUnknownMeddWeight(
    settings?.unknown_meddpicc_default_weight
  ),
});

export const recomputeAccountRlAggregate = (account, orgSettings = {}) => {
  if (!account?.pairs?.length) return account;
  const rlAggregate = calculateRlAggregate(
    account.pairs,
    normalizeAggregateSettings(orgSettings)
  );
  if (rlAggregate === account.rlAggregate) return account;
  return { ...account, rlAggregate };
};

/** Strip hydrated matrix fields so overview API is the sole pair/contact source. */
export const accountShellForOverviewHydrate = account => ({
  id: String(account.id),
  name: account.name,
  initials: account.initials,
  industry: account.industry,
  tier: account.tier,
  region: account.region,
  alLevel: account.alLevel,
  rlAggregate: account.rlAggregate,
});

export const buildHydratedAccountFromOverviewBundle = (
  account,
  { view, summary },
  aggregateSettings = {}
) => {
  const apiContacts = (view.contacts || []).map(c => ({
    ...mapViewContact(c),
    accountId: account.id,
  }));
  const stakeholders = (view.vendors || []).map(v => ({
    ...mapViewVendor(v),
    accountId: account.id,
  }));
  const summaryByPair = new Map(
    (summary.pairs || []).map(p => [String(p.pairId), p])
  );

  const viewPairs = (view.pairs || []).map(p => mapViewPair(p, account.id));
  const accountPairById = new Map(
    (account.pairs || []).map(p => [String(p.id), normalizePairVendorFields(p)])
  );

  let basePairs;
  if (viewPairs.length) {
    basePairs = viewPairs.map(vp => {
      const prev = accountPairById.get(String(vp.id));
      if (!prev) return vp;
      return normalizePairVendorFields({
        ...prev,
        ...vp,
        contactId: prefer(vp.contactId, prev.contactId),
        raw: { ...prev.raw, ...vp.raw },
      });
    });
  } else {
    basePairs = [];
  }

  const pairs = basePairs.map(pair =>
    mergePairFromRlSummary(pair, summaryByPair.get(String(pair.id)))
  );

  const knownIds = new Set(pairs.map(p => p.id));
  for (const vp of viewPairs) {
    const id = String(vp.id);
    if (knownIds.has(id)) continue;
    pairs.push(mergePairFromRlSummary(vp, summaryByPair.get(id)));
    knownIds.add(id);
  }

  const rlAggregate =
    summary.rlAggregate ?? calculateRlAggregate(pairs, aggregateSettings);

  return mergeAccountRelationshipBundle(account, {
    contacts: apiContacts,
    stakeholders,
    pairs,
    rlAggregate,
    source: "api",
    bridgeSource: view.source,
  });
};

/**
 * Personal-lens aliases for My Relationships (Drop-1 enrichAccountForMyRelationships).
 * Prefer myPairs / existing my* counts — never clobber personal stats with account-wide totals.
 */
export const enrichMyRelationshipAccount = (account, index = 0) => {
  const myPairs = Array.isArray(account.myPairs) ? account.myPairs : null;
  let myMappedPairs;
  let myAssessedPairs;
  let myPendingAssessments;

  if (myPairs) {
    myMappedPairs = myPairs.length;
    myAssessedPairs = myPairs.filter(
      pair => pair.rlLevel !== null && pair.rlLevel !== undefined
    ).length;
    myPendingAssessments = Math.max(0, myMappedPairs - myAssessedPairs);
  } else if (account.myMappedPairs != null || account.myAssessedPairs != null) {
    myMappedPairs = Number(account.myMappedPairs) || 0;
    myAssessedPairs = Number(account.myAssessedPairs) || 0;
    myPendingAssessments =
      account.myPendingAssessments != null
        ? Number(account.myPendingAssessments) || 0
        : Math.max(0, myMappedPairs - myAssessedPairs);
  } else {
    // Pre-hydrate placeholder until user-mappings load.
    myMappedPairs = Number(account.mappedPairs) || 0;
    myAssessedPairs = Number(account.assessedPairs) || 0;
    myPendingAssessments = Number(account.pendingAssessments) || 0;
  }

  let sectorColor = account.sectorColor;
  if (!sectorColor) {
    const palette = [
      "#9333ea",
      "#4f46e5",
      "#0891b2",
      "#16a34a",
      "#d97706",
      "#dc2626",
      "#7c3aed",
      "#2563eb",
    ];
    sectorColor = palette[index % palette.length];
  }
  return {
    ...account,
    myMappedPairs,
    myAssessedPairs,
    myPendingAssessments,
    sectorColor,
  };
};
